import { useMemo, useCallback } from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';

interface LeadershipSalesTableProps {
  data: any[];
  yearX: number;
  yearY: number;
}

const months = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

function formatCurrency(amount: number) {
  if (!amount) return '-';
  return amount.toLocaleString('en-US', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
}

const metrics = [
  { key: 'confirmed', label: 'Confirmed Revenue' },
  { key: 'groups', label: 'Number of Stays' },
  { key: 'conversion', label: 'Group Conversion' },
  { key: 'materialization', label: 'Revenue Materialization' },
  { key: 'final', label: 'Final Revenue' },
];

// Segment to macro section mapping
const segmentToMacroMapping: Record<string, string> = {
  'Ad-hoc Group': 'ADG',
  'Allotment contract': 'ADG',
  'ASIA - Ad-hoc Group': 'ASIA',
  'ASIA  - Ad-hoc Group': 'ASIA',  // Added mapping for two-space version
  'ASIA - Allotment Contract': 'ASIA',
  'ASIA - Group Series': 'ASIA',
  'ASIA - Rate Contract': 'ASIA',
  'Group Series': 'SERIES',
  'Rate Contract': 'ADG'
};

// Type definitions for optimized data structure
interface MonthYearData {
  confirmed: number;
  groups: number; // Changed from Set<string> to number to count stays
  conversion: {
    total: number; // Count of total groups (all statuses)
    booked: number; // Count of booked groups
  };
  materialization: {
    final: number;
  };
  final: number;
}

type KpiAggregates = {
  confirmed: number;
  groups: number;
  conversionTotal: number;
  conversionBooked: number;
  materializationFinal: number;
  final: number;
};

function metricFromAggregates(metric: string, t: KpiAggregates): number {
  switch (metric) {
    case 'confirmed':
      return t.confirmed;
    case 'groups':
      return t.groups;
    case 'conversion':
      return t.conversionTotal > 0 ? (t.conversionBooked / t.conversionTotal) * 100 : 0;
    case 'materialization':
      return t.confirmed > 0 ? (t.materializationFinal / t.confirmed) * 100 : 0;
    case 'final':
      return t.final;
    default:
      return 0;
  }
}

function emptyKpiAggregates(): KpiAggregates {
  return {
    confirmed: 0,
    groups: 0,
    conversionTotal: 0,
    conversionBooked: 0,
    materializationFinal: 0,
    final: 0,
  };
}

function addMonthDataToAggregates(t: KpiAggregates, monthData: MonthYearData) {
  t.confirmed += monthData.confirmed;
  t.groups += monthData.groups;
  t.conversionTotal += monthData.conversion.total;
  t.conversionBooked += monthData.conversion.booked;
  t.materializationFinal += monthData.materialization.final;
  t.final += monthData.final;
}

interface MacroSectionData {
  segments: Set<string>;
  months: Record<number, Record<number, MonthYearData>>;
}

export default function LeadershipSalesTable({ data, yearX, yearY }: LeadershipSalesTableProps) {
  // Pre-process and aggregate data in a single pass
  const aggregatedData = useMemo(() => {
    if (!data || data.length === 0) {
      return {};
    }

    const result: Record<string, MacroSectionData> = {};
    
    // Single pass through all data
    data.forEach(row => {
      const { segment, arrival_month, arrival_year, status, estimated_revenue, realised_revenue, request_id } = row;
      
      // Skip rows without required data
      if (!segment || !arrival_month || !arrival_year || !request_id) {
        return;
      }
      
      // Determine macro section
      const macroSection = segmentToMacroMapping[segment];
      
      // Skip unmapped segments - they won't contribute to any calculations
      if (!macroSection || macroSection === 'No Mapping') {
        return;
      }
      
      // Initialize macro section if it doesn't exist
      if (!result[macroSection]) {
        result[macroSection] = {
          segments: new Set(),
          months: {}
        };
      }
      
      // Add segment to macro section
      result[macroSection].segments.add(segment);
      
      // Initialize month if it doesn't exist
      if (!result[macroSection].months[arrival_month]) {
        result[macroSection].months[arrival_month] = {};
      }
      
              // Initialize year if it doesn't exist
        if (!result[macroSection].months[arrival_month][arrival_year]) {
          result[macroSection].months[arrival_month][arrival_year] = {
            confirmed: 0,
            groups: 0, // Changed from new Set() to 0 to count stays
            conversion: {
              total: 0, // Count of total groups (all statuses)
              booked: 0  // Count of booked groups
            },
            materialization: {
              final: 0
            },
            final: 0
          };
        }
      
      const monthData = result[macroSection].months[arrival_month][arrival_year];
      
      // Always count total groups (all statuses) - each row represents a group
      monthData.conversion.total += 1;
      
      // Add to booked metrics only for Booked status
      if (status === 'Booked') {
        monthData.confirmed += estimated_revenue || 0;
        monthData.groups += 1; // Count each stay instead of unique groups
        monthData.conversion.booked += 1; // Count booked groups
        monthData.materialization.final += realised_revenue || 0;
        monthData.final += realised_revenue || 0;
      }
    });
    
    return result;
  }, [data]);

  // Get all segments and group them by macro sections (for display purposes)
  const macroSections = useMemo(() => {
    // Build a grouped mapping from segmentToMacroMapping, not just from data
    const grouped: Record<string, string[]> = {};
    Object.entries(segmentToMacroMapping).forEach(([segment, macroSection]) => {
      if (!grouped[macroSection]) grouped[macroSection] = [];
      grouped[macroSection].push(segment);
    });
    return grouped;
  }, []);

  // Optimized value getter using pre-processed data
  const getValue = useCallback((macroSection: string, metric: string, month: number, year: number): number => {
    const monthData = aggregatedData[macroSection]?.months[month]?.[year];
    if (!monthData) return 0;
    
    switch (metric) {
      case 'confirmed':
        return monthData.confirmed;
      case 'groups':
        return monthData.groups;
      case 'conversion':
        return monthData.conversion.total > 0 
          ? (monthData.conversion.booked / monthData.conversion.total) * 100 
          : 0;
      case 'materialization':
        return monthData.confirmed > 0 
          ? (monthData.materialization.final / monthData.confirmed) * 100 
          : 0;
      case 'final':
        return monthData.final;
      default:
        return 0;
    }
  }, [aggregatedData]);

  /** Sum KPIs across all macro segments for a month/year (ratios recomputed from totals). */
  const getTotalValue = useCallback((metric: string, month: number, year: number): number => {
    const totals = emptyKpiAggregates();

    for (const macroSection of Object.keys(aggregatedData)) {
      const monthData = aggregatedData[macroSection]?.months[month]?.[year];
      if (!monthData) continue;
      addMonthDataToAggregates(totals, monthData);
    }

    return metricFromAggregates(metric, totals);
  }, [aggregatedData]);

  const getAnnualValue = useCallback((macroSection: string, metric: string, year: number): number => {
    const totals = emptyKpiAggregates();

    for (let month = 1; month <= 12; month++) {
      const monthData = aggregatedData[macroSection]?.months[month]?.[year];
      if (!monthData) continue;
      addMonthDataToAggregates(totals, monthData);
    }

    return metricFromAggregates(metric, totals);
  }, [aggregatedData]);

  const getAnnualTotalValue = useCallback((metric: string, year: number): number => {
    const totals = emptyKpiAggregates();

    for (let month = 1; month <= 12; month++) {
      for (const macroSection of Object.keys(aggregatedData)) {
        const monthData = aggregatedData[macroSection]?.months[month]?.[year];
        if (!monthData) continue;
        addMonthDataToAggregates(totals, monthData);
      }
    }

    return metricFromAggregates(metric, totals);
  }, [aggregatedData]);

  const renderMonthCellsForMetric = (
    metricKey: string,
    valueGetter: (metric: string, month: number, year: number) => number,
    keyPrefix: string,
    totalRow = false,
  ) =>
    months.flatMap((_, i) => {
      const month = i + 1;
      const value = valueGetter(metricKey, month, yearX);
      const valueY = valueGetter(metricKey, month, yearY);
      const baseX = totalRow ? 'bg-slate-100' : 'bg-blue-50';
      if (metricKey === 'confirmed' || metricKey === 'final') {
        return [
          <td key={`${keyPrefix}-x-${i}`} className={`border px-2 py-1 text-center ${baseX}`}>{formatCurrency(value)}</td>,
          <td key={`${keyPrefix}-y-${i}`} className={`border px-2 py-1 text-center ${totalRow ? 'bg-slate-50' : ''}`}>{formatCurrency(valueY)}</td>,
        ];
      }
      if (metricKey === 'conversion' || metricKey === 'materialization') {
        return [
          <td key={`${keyPrefix}-x-${i}`} className={`border px-2 py-1 text-center ${baseX}`}>{value > 0 ? `${value.toFixed(1)}%` : '-'}</td>,
          <td key={`${keyPrefix}-y-${i}`} className={`border px-2 py-1 text-center ${totalRow ? 'bg-slate-50' : ''}`}>{valueY > 0 ? `${valueY.toFixed(1)}%` : '-'}</td>,
        ];
      }
      return [
        <td key={`${keyPrefix}-x-${i}`} className={`border px-2 py-1 text-center ${baseX}`}>{value || '-'}</td>,
        <td key={`${keyPrefix}-y-${i}`} className={`border px-2 py-1 text-center ${totalRow ? 'bg-slate-50' : ''}`}>{valueY || '-'}</td>,
      ];
    });

  const renderAnnualCellsForMetric = (
    metricKey: string,
    valueGetter: (metric: string, year: number) => number,
    keyPrefix: string,
    totalRow = false,
  ) => {
    const value = valueGetter(metricKey, yearX);
    const valueY = valueGetter(metricKey, yearY);
    const baseX = totalRow ? 'bg-slate-100' : 'bg-blue-50';
    const yearBorder = 'border-l-2 border-gray-300';
    if (metricKey === 'confirmed' || metricKey === 'final') {
      return [
        <td key={`${keyPrefix}-annual-x`} className={`border px-2 py-1 text-center ${yearBorder} ${baseX}`}>{formatCurrency(value)}</td>,
        <td key={`${keyPrefix}-annual-y`} className={`border px-2 py-1 text-center ${totalRow ? 'bg-slate-50' : ''}`}>{formatCurrency(valueY)}</td>,
      ];
    }
    if (metricKey === 'conversion' || metricKey === 'materialization') {
      return [
        <td key={`${keyPrefix}-annual-x`} className={`border px-2 py-1 text-center ${yearBorder} ${baseX}`}>{value > 0 ? `${value.toFixed(1)}%` : '-'}</td>,
        <td key={`${keyPrefix}-annual-y`} className={`border px-2 py-1 text-center ${totalRow ? 'bg-slate-50' : ''}`}>{valueY > 0 ? `${valueY.toFixed(1)}%` : '-'}</td>,
      ];
    }
    return [
      <td key={`${keyPrefix}-annual-x`} className={`border px-2 py-1 text-center ${yearBorder} ${baseX}`}>{value || '-'}</td>,
      <td key={`${keyPrefix}-annual-y`} className={`border px-2 py-1 text-center ${totalRow ? 'bg-slate-50' : ''}`}>{valueY || '-'}</td>,
    ];
  };
  const segmentColWidth = '8.5rem';
  const metricColWidth = '12rem';
  const headerHeight = '2.25rem';

  // If no data, show empty state
  if (!data || data.length === 0) {
    return (
      <div className="p-8 text-center text-gray-500">
        <div className="text-lg font-semibold mb-2">Please wait for data to load</div>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-xs border">
        <thead>
          <tr>
            <th
              className="border px-2 py-1 bg-gray-50 sticky left-0 top-0 z-30"
              rowSpan={2}
              style={{ minWidth: segmentColWidth, left: 0, height: headerHeight }}
            >
              Macro Segment
            </th>
            <th
              className="border px-2 py-1 bg-gray-50 sticky left-[8.5rem] top-0 z-20"
              rowSpan={2}
              style={{ minWidth: metricColWidth, left: '8.5rem', height: headerHeight }}
            >
              KPIs
            </th>
            {months.map((m) => (
              <th
                key={m}
                className="border px-2 py-1 bg-gray-50 sticky top-0 z-10"
                colSpan={2}
                style={{ height: headerHeight }}
              >
                {m}
              </th>
            ))}
            <th
              className="border border-l-2 border-l-gray-300 px-2 py-1 bg-gray-50 sticky top-0 z-10"
              colSpan={2}
              style={{ height: headerHeight }}
            >
              Total
            </th>
          </tr>
          <tr>
            {months.map((_, i) => [
              <th
                key={`x-${i}`}
                className="border px-2 py-1 bg-gray-50 sticky top-[2.25rem] z-10"
              >
                {yearX}
              </th>,
              <th
                key={`y-${i}`}
                className="border px-2 py-1 bg-gray-50 sticky top-[2.25rem] z-10"
              >
                {yearY}
              </th>
            ])}
            <th
              key="annual-x"
              className="border border-l-2 border-l-gray-300 px-2 py-1 bg-gray-50 sticky top-[2.25rem] z-10"
            >
              {yearX}
            </th>
            <th
              key="annual-y"
              className="border px-2 py-1 bg-gray-50 sticky top-[2.25rem] z-10"
            >
              {yearY}
            </th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(macroSections).map(([macroSection, segments], sectionIndex) => (
            metrics.map((metric, metricIdx) => (
              <tr 
                key={macroSection + '-' + metric.key} 
                className={`${
                  sectionIndex % 2 === 1 && metricIdx === 0 ? 'border-t-2 border-blue-200' : ''
                } ${
                  sectionIndex % 2 === 1 && metricIdx === metrics.length - 1 ? 'border-b-2 border-blue-200' : ''
                }`}
              >
                {metricIdx === 0 && (
                  <td
                    className="border px-2 py-1 font-bold sticky left-0 z-20 bg-gray-100"
                    rowSpan={metrics.length}
                    style={{ minWidth: segmentColWidth, left: 0, verticalAlign: 'middle' }}
                  >
                    <div className="flex items-center gap-1">
                      <span className="text-sm">{macroSection}</span>
                      <div className="relative group">
                        <InformationCircleIcon className="h-4 w-4 text-blue-500 cursor-help" />
                        <div className="absolute left-full top-1/2 transform -translate-y-1/2 ml-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                          <div className="font-semibold mb-1">Segments:</div>
                          {segments.length > 0 ? (
                            segments.map((segment, idx) => (
                              <div key={idx} className="text-gray-200">• {segment}</div>
                            ))
                          ) : (
                            <div className="text-gray-200">No segments mapped</div>
                          )}
                          <div className="absolute top-1/2 left-0 transform -translate-y-1/2 -translate-x-1 w-0 h-0 border-t-4 border-b-4 border-r-4 border-transparent border-r-gray-900"></div>
                        </div>
                      </div>
                    </div>
                  </td>
                )}
                <td
                  className="border px-2 py-1 font-semibold sticky left-[8.5rem] z-10 bg-white"
                  style={{ minWidth: metricColWidth, left: '8.5rem' }}
                >
                  {metric.label}
                </td>
                {renderMonthCellsForMetric(
                  metric.key,
                  (m, month, year) => getValue(macroSection, m, month, year),
                  `${macroSection}-${metric.key}`,
                )}
                {renderAnnualCellsForMetric(
                  metric.key,
                  (m, year) => getAnnualValue(macroSection, m, year),
                  `${macroSection}-${metric.key}`,
                )}
              </tr>
            ))
          ))}
          {metrics.map((metric, metricIdx) => (
            <tr
              key={`total-${metric.key}`}
              className={metricIdx === 0 ? 'border-t-2 border-gray-800' : ''}
            >
              {metricIdx === 0 && (
                <td
                  className="border px-2 py-1 font-bold sticky left-0 z-20 bg-slate-200"
                  rowSpan={metrics.length}
                  style={{ minWidth: segmentColWidth, left: 0, verticalAlign: 'middle' }}
                >
                  <span className="text-sm">Total</span>
                </td>
              )}
                <td
                  className="border px-2 py-1 sticky left-[8.5rem] z-10 bg-slate-100"
                  style={{ minWidth: metricColWidth, left: '8.5rem' }}
                >
                  {metric.label}
                </td>
              {renderMonthCellsForMetric(
                metric.key,
                getTotalValue,
                `total-${metric.key}`,
                true,
              )}
              {renderAnnualCellsForMetric(
                metric.key,
                getAnnualTotalValue,
                `total-${metric.key}`,
                true,
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
